// src/components/admin/quotes/QuoteManagement.jsx
import { useState } from 'react';
import {
  FaFileAlt,
  FaClock,
  FaExclamationTriangle,
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaGlobe,
  FaCommentAlt,
} from 'react-icons/fa';
import useAdminQuotes from '../../../hooks/useAdminQuotes';
import AdminQuoteList from './AdminQuoteList';

const QuoteManagement = () => {
  const {
    quotes,
    loading,
    error,
    deleteQuote,
    getQuoteStats,
    setError
  } = useAdminQuotes();

  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDetails, setShowQuoteDetails] = useState(false);

  const stats = getQuoteStats();

  const getCustomerName = (quote) => {
    if (quote.customerName) return quote.customerName;
    if (quote.firstName || quote.lastName) {
      return `${quote.firstName || ''} ${quote.lastName || ''}`.trim() || 'Unknown Customer';
    }
    return quote.name || 'Unknown Customer';
  };

  const getServiceName = (quote) => {
    if (quote.service?.name) return quote.service.name;
    if (quote.serviceRequested) {
      const serviceMap = {
        'general-inquiry': 'General Inquiry',
        'landscaping': 'Landscaping',
        'lawn-care': 'Lawn Care',
        'hardscaping': 'Hardscaping',
        'maintenance': 'Maintenance',
        'design': 'Design Services'
      };
      return serviceMap[quote.serviceRequested] || quote.serviceRequested;
    }
    return 'N/A';
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getSourceDisplay = (quote) => {
    if (quote.source) {
      const sourceMap = {
        'website_form': 'Website Form',
        'phone_call': 'Phone Call',
        'email': 'Email',
        'referral': 'Referral',
        'admin_created': 'Admin Created'
      };
      return sourceMap[quote.source] || quote.source.replace('_', ' ');
    }
    return 'N/A';
  };

  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setShowQuoteDetails(true);
  };

  const handleDeleteQuote = async (quoteId) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (window.confirm(`Are you sure you want to delete the quote for ${getCustomerName(quote)}?`)) {
      try {
        await deleteQuote(quoteId);
      } catch (err) {
        setError('Failed to delete quote');
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Quote Management</h1>
        <p className="text-gray-600">Review and manage customer quote requests.</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <FaExclamationTriangle className="w-5 h-5 text-red-400 mr-2" />
            <p className="text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-500">
              <FaFileAlt className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Quotes</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalQuotes}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-yellow-500">
              <FaClock className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Last 24 Hours</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.last24Hours}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-purple-500">
              <FaCalendarAlt className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Last 7 Days</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.lastWeek}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-600">
              <FaCalendarAlt className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Last 30 Days</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.lastMonth}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quote List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Quotes</h3>
        </div>
        <div className="p-6">
          <AdminQuoteList
            quotes={quotes}
            loading={loading}
            onView={handleViewQuote}
            onDelete={handleDeleteQuote}
          />
        </div>
      </div>

      {/* Quote Details Modal */}
      {showQuoteDetails && selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Quote Details</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Submitted {formatDateTime(selectedQuote.createdAt || selectedQuote.requestedAt)}
                </p>
              </div>
              <button
                onClick={() => { setShowQuoteDetails(false); setSelectedQuote(null); }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-6">
              {/* Customer Info */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 flex items-center mb-4">
                  <FaUser className="w-5 h-5 mr-2 text-gray-500" />
                  Customer Information
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Customer Name</label>
                    <p className="text-base text-gray-900">{getCustomerName(selectedQuote)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email Address</label>
                    <p className="text-base text-gray-900 flex items-center">
                      <FaEnvelope className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedQuote.customerEmail || selectedQuote.email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone Number</label>
                    <p className="text-base text-gray-900 flex items-center">
                      <FaPhone className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedQuote.customerPhone || selectedQuote.phone || 'N/A'}
                    </p>
                  </div>
                  {(selectedQuote.propertyAddress || selectedQuote.address) && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Property Address</label>
                      <p className="text-base text-gray-900">
                        {selectedQuote.propertyAddress || selectedQuote.address}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Request Info */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-lg font-medium text-gray-900 flex items-center mb-4">
                  <FaCalendarAlt className="w-5 h-5 mr-2 text-gray-500" />
                  Request Information
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Date & Time of Request</label>
                    <p className="text-base text-gray-900">
                      {formatDateTime(selectedQuote.createdAt || selectedQuote.requestedAt)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                      selectedQuote.status === 'new' ? 'bg-blue-100 text-blue-800' :
                      selectedQuote.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      selectedQuote.status === 'approved' ? 'bg-green-100 text-green-800' :
                      selectedQuote.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedQuote.status?.replace('_', ' ') || 'new'}
                    </span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Service Requested</label>
                    <p className="text-base text-gray-900">{getServiceName(selectedQuote)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Source</label>
                    <p className="text-base text-gray-900 flex items-center">
                      <FaGlobe className="w-4 h-4 mr-2 text-gray-400" />
                      {getSourceDisplay(selectedQuote)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Message */}
              {(selectedQuote.message || selectedQuote.service?.description) && (
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center mb-4">
                    <FaCommentAlt className="w-5 h-5 mr-2 text-gray-500" />
                    Customer Message
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {selectedQuote.message || selectedQuote.service?.description}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => { setShowQuoteDetails(false); setSelectedQuote(null); }}
                className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteManagement;